import React from 'react';
import { Target, AlertCircle } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';

/**
 * TodaysFocus - Compact card showing top 3 tasks for the day
 *
 * Priority order:
 * 1. Overdue tasks (urgent)
 * 2. Tasks due today (urgent)
 * 3. Tasks due tomorrow (heads up)
 * 4. "Now" priority tasks (fill remaining slots)
 *
 * Displayed as a compact card matching Tasks/Kitchen card style
 */

// Helper to check if a date is today
function isToday(dateString) {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

// Helper to check if a date is tomorrow
function isTomorrow(dateString) {
  if (!dateString) return false;
  const date = new Date(dateString);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return date.toDateString() === tomorrow.toDateString();
}

// Helper to get deadline status
function getDeadlineStatus(dateString) {
  if (!dateString) return null;
  if (isToday(dateString)) return 'today';
  if (isTomorrow(dateString)) return 'tomorrow';

  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (date < today) return 'overdue';
  return null;
}

// Sort tasks: overdue → today → tomorrow → Now priority
function sortByDeadlinePriority(tasks) {
  return [...tasks].sort((a, b) => {
    const statusA = getDeadlineStatus(a.deadline);
    const statusB = getDeadlineStatus(b.deadline);

    // Priority order: overdue (0), today (1), tomorrow (2), Now priority (3), other (4)
    const getPriorityScore = (task, status) => {
      if (status === 'overdue') return 0;
      if (status === 'today') return 1;
      if (status === 'tomorrow') return 2;
      if (task.priority === 'Now') return 3;
      return 4;
    };

    const scoreA = getPriorityScore(a, statusA);
    const scoreB = getPriorityScore(b, statusB);

    if (scoreA !== scoreB) return scoreA - scoreB;

    // If same score and both have deadlines, sort by date
    if (a.deadline && b.deadline) {
      return new Date(a.deadline) - new Date(b.deadline);
    }

    // Deadlined tasks before non-deadlined
    if (a.deadline && !b.deadline) return -1;
    if (!a.deadline && b.deadline) return 1;

    return 0;
  });
}

// Get tasks that should show in Focus Mode
function getTopTasks(allTasks, maxCount = 3) {
  // Filter to active tasks only
  const activeTasks = allTasks.filter(t => !t.done);

  // Get tasks that are candidates for today's focus:
  // - Due today/tomorrow/overdue
  // - OR "Now" priority
  const candidates = activeTasks.filter(t => {
    const status = getDeadlineStatus(t.deadline);
    return status === 'overdue' || status === 'today' || status === 'tomorrow' || t.priority === 'Now';
  });

  // Sort and take top N
  const sorted = sortByDeadlinePriority(candidates);
  return sorted.slice(0, maxCount);
}

export default function TodaysFocus({ tasks, onNavigate }) {
  // Get top tasks sorted by deadline priority
  const topTasks = getTopTasks(tasks, 3);

  // Check if any tasks are overdue
  const hasOverdue = topTasks.some(t => getDeadlineStatus(t.deadline) === 'overdue');

  // Get subtitle text
  const getSubtitle = () => {
    if (topTasks.length === 0) return 'Ready when you are';
    if (topTasks.length === 1) return '1 priority item';
    return `${topTasks.length} priority items`;
  };

  return (
    <button
      onClick={() => onNavigate('tasks')}
      style={{
        padding: '16px 12px',
        background: ABLE_COLORS.neutral,
        border: 'none',
        borderRadius: ABLE_COLORS.cardRadius,
        boxShadow: ABLE_COLORS.cardShadow,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        transition: 'all 0.15s ease',
        width: '100%'
      }}
    >
      {hasOverdue ? (
        <AlertCircle size={24} color={ABLE_COLORS.semantic?.error || '#EF4444'} />
      ) : (
        <Target size={24} color={ABLE_COLORS.primary} />
      )}
      <span style={{
        fontSize: '13px',
        fontWeight: '600',
        color: ABLE_COLORS.textPrimary
      }}>
        Focus Mode
      </span>
      <span style={{
        fontSize: '10px',
        color: hasOverdue ? (ABLE_COLORS.semantic?.error || '#EF4444') : ABLE_COLORS.textMuted
      }}>
        {getSubtitle()}
      </span>
    </button>
  );
}
